import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import { useCallback, useState } from "react";
import { DatePicker } from "../../components/DatePicker";
import { Autocomplete } from "../../components/Autocomplete";
import { useStyles } from "./styles";
import Stack from "@mui/material/Stack";
import { Search } from "@mui/icons-material";

import { Overview } from "./Overview";
import TabPanel from "../../components/TabPanel";
import {
  listOverviewOffersDay,
  listOverviewOffersHour,
  listOverviewOffersStore,
  listOverviewOffers,
  listResultsOffers,
} from "../../services/offers";
import { useQuery } from "react-query";
import { subMonths } from "date-fns";
import { List } from "./List";
import { listStores } from "../../services/insights";
import { Store } from "../../types/insights";

export const ResultsOffers = () => {
  const styles = useStyles();

  const [activeView, setActiveView] = useState<number>(0);
  const [startDate, setStartDate] = useState<Date>(subMonths(new Date(), 1));
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [store, setStore] = useState<Store | null>(null);
  const [offerName, setOfferName] = useState<string>();

  const handleChangeView = useCallback((view: number) => {
    setActiveView(view);
  }, []);

  const stores = useQuery("stores", listStores);

  const overviewOffers = useQuery("overviewOffers", async () => {
    const response = await listOverviewOffers(
      startDate,
      endDate,
      store?.id_loja,
      offerName
    );

    return response;
  });

  const overviewOffersDay = useQuery("overviewOffersDay", async () => {
    const response = await listOverviewOffersDay(
      startDate,
      endDate,
      store?.id_loja,
      offerName
    );

    return response;
  });

  const overviewOffersHour = useQuery("overviewOffersHour", async () => {
    const response = await listOverviewOffersHour(
      startDate,
      endDate,
      store?.id_loja,
      offerName
    );

    return response;
  });

  const overviewOffersStore = useQuery("overviewOffersStore", async () => {
    const response = await listOverviewOffersStore(
      startDate,
      endDate,
      store?.id_loja,
      offerName
    );

    return response;
  });

  const resultsOffers = useQuery("resultsOffers", async () => {
    const response = await listResultsOffers(
      startDate,
      endDate,
      store?.id_loja,
      offerName
    );

    return response;
  });

  const handleRefetch = useCallback(() => {
    resultsOffers.refetch();
    overviewOffers.refetch();
    overviewOffersDay.refetch();
    overviewOffersHour.refetch();
    overviewOffersStore.refetch();
  }, [
    resultsOffers,
    overviewOffers,
    overviewOffersDay,
    overviewOffersHour,
    overviewOffersStore,
  ]);

  return (
    <>
      <Stack spacing={4}>
        <Stack className={styles.root}>
          <Typography variant="h4">
            Resultados dos descontos personalizados
          </Typography>
          <Typography variant="body2">
            Aqui ficam todos os resultados que seus descontos personalizados
            tiveram.
          </Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" spacing={1}>
            <DatePicker
              fullWidth
              size="small"
              label="Data de início"
              value={startDate}
              onChange={(date) => setStartDate(date as Date)}
            />
            <DatePicker
              fullWidth
              size="small"
              label="Data de término"
              value={endDate}
              onChange={(date) => setEndDate(date as Date)}
            />
            <Autocomplete
              fullWidth
              size="small"
              label="Loja"
              options={stores.data}
              getOptionLabel={(option) => option.nome_loja}
              onChange={(_, option) => setStore(option)}
            />
            <TextField
              label="Nome"
              onChange={({ target: { value } }) => setOfferName(value)}
              fullWidth
              size="small"
            />
            <Button
              onClick={handleRefetch}
              fullWidth
              startIcon={<Search />}
              color="primary"
            >
              Buscar
            </Button>
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" spacing={1}>
            <Button
              onClick={() => handleChangeView(0)}
              variant={activeView === 0 ? "contained" : "outlined"}
            >
              Visão geral
            </Button>
            <Button
              onClick={() => handleChangeView(1)}
              variant={activeView === 1 ? "contained" : "outlined"}
            >
              Ofertas
            </Button>
          </Stack>
        </Stack>
        <TabPanel index={0} value={activeView}>
          <Overview
            store={overviewOffersStore.data}
            day={overviewOffersDay.data}
            hours={overviewOffersHour.data}
            ageGroup={overviewOffers.data?.faixaEtaria}
            genre={overviewOffers.data?.genero}
            overview={overviewOffers.data?.visaoGeral}
            loading={
              overviewOffers.isFetching ||
              overviewOffersDay.isFetching ||
              overviewOffersHour.isFetching
            }
          />
        </TabPanel>
        <TabPanel index={1} value={activeView}>
          <List loading={resultsOffers.isFetching} data={resultsOffers.data} />
        </TabPanel>
      </Stack>
    </>
  );
};
